import React, { forwardRef, memo, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { dhEye, dhEyeSlash, vsGripper } from '@deephaven/icons';
import { Button, Tooltip } from '@deephaven/components';
import VisibilityOrderingGroup from './VisibilityOrderingGroup';
import { type FlattenedIrisGridTreeItem } from './sortable-tree/utilities';
import type ColumnHeaderGroup from '../../ColumnHeaderGroup';

type VisibilityOrderingItemProps = {
  value: string;
  clone: boolean;
  childCount: number;
  item: FlattenedIrisGridTreeItem;
  onVisibilityChange: (modelIndexes: number[], isVisible: boolean) => void;
  /**
   * If true, allows changing visibility by clicking and dragging over multiple item
   * visibility buttons.
   */
  visibilityClickAndDrag: boolean;
  onClick: (name: string, event: React.MouseEvent<HTMLElement>) => void;
  onGroupDelete: (group: ColumnHeaderGroup) => void;
  onGroupColorChange: (
    group: ColumnHeaderGroup,
    color: string | undefined
  ) => void;
  onGroupNameChange: (group: ColumnHeaderGroup, name: string) => void;
  validateGroupName: (name: string) => string;
  handleProps?: Record<string, unknown>;
};

function emptyOnClick(): void {
  // no-op
}

const VisibilityOrderingItem = forwardRef<
  HTMLDivElement,
  VisibilityOrderingItemProps
>(function VisibilityOrderingItem(props, ref) {
  const {
    value,
    clone,
    childCount,
    item,
    onVisibilityChange,
    visibilityClickAndDrag,
    onClick,
    onGroupDelete,
    onGroupColorChange,
    onGroupNameChange,
    validateGroupName,
    handleProps,
  } = props;
  const { group, modelIndex, isVisible } = item.data;
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleVisibilityChange = useCallback(
    (e: React.MouseEvent) => {
      if (e.buttons === 1) {
        onVisibilityChange([modelIndex].flat(), !isVisible);
        buttonRef.current?.focus();
      }
    },
    [onVisibilityChange, modelIndex, isVisible]
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      onClick(value, event);
    },
    [onClick, value]
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={ref}
      className={classNames('tree-item', {
        isSelected: item.selected,
        'two-dragged': childCount === 2,
        'multiple-dragged': childCount > 2,
      })}
      onClick={handleClick}
      data-index={item.index}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...handleProps}
    >
      {!clone && (
        <Button
          ref={buttonRef}
          kind="ghost"
          className="px-1"
          // We PropType validate onClick for Button
          onClick={emptyOnClick}
          onMouseDown={handleVisibilityChange}
          onMouseEnter={
            visibilityClickAndDrag ? handleVisibilityChange : undefined
          }
          icon={isVisible ? dhEye : dhEyeSlash}
          tooltip="Toggle visibility"
        />
      )}
      <span
        title={value}
        className={classNames('column-name', group && 'column-group')}
      >
        {/* Display a normal item if this is the drag overlay clone, even if it's a group */}
        {group && !clone ? (
          <VisibilityOrderingGroup
            group={group}
            onDelete={onGroupDelete}
            onColorChange={onGroupColorChange}
            onNameChange={onGroupNameChange}
            validateName={validateGroupName}
          />
        ) : (
          value
        )}
      </span>
      <div>
        {clone && childCount > 1 && (
          <span className="item-count">{childCount}</span>
        )}
        {!clone && (
          <>
            <Tooltip>Drag to re-order</Tooltip>
            <FontAwesomeIcon icon={vsGripper} />
          </>
        )}
      </div>
    </div>
  );
});

const MemoizedVisibilityOrderingItem = memo(VisibilityOrderingItem);

export default MemoizedVisibilityOrderingItem;
